import { useState } from 'react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { useLockBodyScroll } from './useLockBodyScroll';

export default function AddContactModal({
  isOpen,
  onClose,
  onAdd,
  trads,
  authorizedCountries,
}) {
  useLockBodyScroll(isOpen);

  console.log('authorizedCountries', authorizedCountries);
  const isFrench = window.location.pathname.startsWith('/fr');
  // If we have authorized countries and the default is not in it, pick the first one
  let defaultCountry = isFrench ? 'fr' : 'us';

  if (authorizedCountries && authorizedCountries.length > 0) {
    if (!authorizedCountries.includes(defaultCountry.toUpperCase())) {
      defaultCountry = authorizedCountries[0].toLowerCase();
    }
  }

  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState(defaultCountry);
  const [error, setError] = useState('');

  const t = trads?.contacts || {};

  // ... (rest of the component state/handlers)

  const allCountries = [
    ['United States', 'us', '1'],
    ['Canada', 'ca', '1'],
    ['Austria', 'at', '43'],
    ['Belgium', 'be', '32'],
    ['Bulgaria', 'bg', '359'],
    ['Croatia', 'hr', '385'],
    ['Cyprus', 'cy', '357'],
    ['Czech Republic', 'cz', '420'],
    ['Denmark', 'dk', '45'],
    ['Estonia', 'ee', '372'],
    ['Finland', 'fi', '358'],
    ['France', 'fr', '33'],
    ['Germany', 'de', '49'],
    ['Greece', 'gr', '30'],
    ['Hungary', 'hu', '36'],
    ['Ireland', 'ie', '353'],
    ['Italy', 'it', '39'],
    ['Latvia', 'lv', '371'],
    ['Lithuania', 'lt', '370'],
    ['Luxembourg', 'lu', '352'],
    ['Malta', 'mt', '356'],
    ['Netherlands', 'nl', '31'],
    ['Poland', 'pl', '48'],
    ['Portugal', 'pt', '351'],
    ['Romania', 'ro', '40'],
    ['Slovakia', 'sk', '421'],
    ['Slovenia', 'si', '386'],
    ['Spain', 'es', '34'],
    ['Sweden', 'se', '46'],
  ];

  const filteredCountries =
    authorizedCountries && authorizedCountries.length > 0
      ? allCountries.filter((c) =>
          authorizedCountries.includes(c[1].toUpperCase())
        )
      : allCountries;

  const handleClose = () => {
    setPhone('');
    setName('');
    setCountryCode(defaultCountry);
    setError('');
    onClose();
  };

  const handleAdd = async () => {
    if (!phone || !phone.trim()) return;
    setError('');

    try {
      const res = await fetch('/api/contacts/update-or-create-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          phone,
          countryCode: countryCode.toUpperCase(),
        }),
      });

      const data = await res.json();
      if (data.success && data.contact) {
        onAdd(data); // Pass full data to handle verificationPending
        handleClose();
      } else {
        // API returns translated error message, use it directly
        setError(data.error || t.failedToAddContact || 'Failed to add contact');
      }
    } catch (err) {
      console.error('Error adding contact:', err);
      setError(t.errorAddingContact || 'Error adding contact');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            {t.addContactTitle || 'Add Contact'}
          </h2>
          <button
            type="button"
            onClick={handleClose}
            data-testid="modal-close-btn"
            className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
          >
            ×
          </button>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          {t.addContactSubtitle ||
            'Input phone number and name of the contact, later you will be able to send text messages to your contacts'}
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.phoneNumber || 'Phone number'}
            </label>
            <PhoneInput
              defaultCountry={defaultCountry}
              value={phone}
              onChange={(value, meta) => {
                setPhone(value);
                if (meta?.country?.iso2) {
                  setCountryCode(meta.country.iso2);
                }
              }}
              countries={filteredCountries}
              placeholder={t.phoneNumberPlaceholder || 'Enter phone number'}
              data-testid="new-phone-input"
              inputClassName="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t.name || 'Name'}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t.namePlaceholder || 'Contact name'}
              data-testid="new-name-input"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            />
            {name.trim() && phone.startsWith('+1') && (
              <div className="mt-4 mb-4">
                <p className="text-sm text-gray-500 bg-amber-100 text-yellow-700 p-2 rounded-md border border-yellow-400">
                  ⚠️{' '}
                  {(
                    t.optInWarningAddContact ||
                    '{name} will have to opt-in by replying "yes" to the opt SMS message that will be sent to him/her'
                  ).replace('{name}', name.trim())}
                </p>
              </div>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={handleClose}
            data-testid="contact-cancel-btn"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg"
          >
            {t.cancel || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={handleAdd}
            data-testid="submit-new-contact-btn"
            className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg"
          >
            {t.addContact || 'Add contact'}
          </button>
        </div>
      </div>
    </div>
  );
}
